import type {DiferenciaDeExportacion, ResultadoDelConsolidado} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCargarConsolidado, type EleccionesDelConsolidado} from '../api/consolidado';
import {useEliminarExportaciones} from '../api/seguimiento';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_CONSOLIDADO} from './ayuda-inventario';
import {ElegirArchivo} from './elegir-archivo';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

const SIN_ELECCIONES: EleccionesDelConsolidado = {corregir: [], meses: [], liberar: []};
const alternar = (lista: string[], valor: string, marcado: boolean) =>
  marcado ? [...new Set([...lista, valor])] : lista.filter(x => x !== valor);

// Cargar el Consolidated Shipments vigente (decision 0035, solo administradores).
// Primero se revisa: Big Bags nuevos, asignaciones que faltan y en que difieren
// el archivo y la app; se elige que corregir como dice el archivo y se guarda.
export function CargarConsolidado({onCerrar}: {onCerrar: () => void}) {
  const cargar = useCargarConsolidado();
  const eliminar = useEliminarExportaciones();
  const permisos = usePermisos();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<ResultadoDelConsolidado | null>(null);
  const [elecciones, setElecciones] = useState<EleccionesDelConsolidado>(SIN_ELECCIONES);
  const [error, setError] = useState<string | null>(null);
  const [borradas, setBorradas] = useState<string | null>(null);

  const enviar = async (modo: 'revisar' | 'aplicar', elegido = archivo, conElecciones = elecciones) => {
    if (!elegido) return;
    setError(null);
    try {
      setResultado(await cargar.mutateAsync({archivo: elegido, modo, elecciones: conElecciones}));
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo leer el archivo.', 'Could not read the file.'));
    }
  };
  const borrarVacias = async (codigos: string[]) => {
    if (
      !window.confirm(
        tr(
          `¿Borrar ${codigos.join(', ')}? Quedaron sin Big Bags. Se borra también su seguimiento.`,
          `Delete ${codigos.join(', ')}? They have no Big Bags left. Their tracking is deleted too.`,
        ),
      )
    )
      return;
    try {
      const r = await eliminar.mutateAsync({modo: 'eliminar', codigos});
      setBorradas(
        tr(
          `Se borraron ${numero(r.eliminadas)} exportaciones.${r.bloqueadas.length ? ` No se borraron: ${r.bloqueadas.map(b => `${b.codigo} (${b.motivo})`).join(', ')}.` : ''}`,
          `${numero(r.eliminadas)} exports deleted.${r.bloqueadas.length ? ` Not deleted: ${r.bloqueadas.map(b => `${b.codigo} (${b.motivo})`).join(', ')}.` : ''}`,
        ),
      );
    } catch (e) {
      setBorradas(e instanceof ErrorDeApi ? e.message : tr('No se pudieron borrar.', 'Could not delete them.'));
    }
  };

  const r = resultado;
  const d = r?.diferencias;
  const asignar = d?.exportaciones.filter(g => g.tipo === 'se_asigna').reduce((n, g) => n + g.bolsas.length, 0) ?? 0;
  const elegidas = elecciones.corregir.length + elecciones.meses.length + elecciones.liberar.length;
  const hayQueGuardar =
    !!r &&
    !r.aplicado &&
    (r.bolsasNuevas > 0 ||
      asignar > 0 ||
      elegidas > 0 ||
      r.bolsasExportadas > 0 ||
      r.bolsasConContenedor > 0 ||
      r.bolsasConEntrada > 0);

  return (
    <section
      aria-label={tr('Cargar Consolidated', 'Upload Consolidated')}
      className="space-y-3 rounded-lg border border-verde/40 bg-white p-4 text-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Cargar el Consolidated Shipments', 'Upload the Consolidated Shipments')}</h2>
          <p className="mt-1 text-apagado">
            {tr(
              'Elige el archivo vigente. La app muestra los Big Bags nuevos, los que el archivo ya asignó y en qué difiere de la app. Marca lo que quieres corregir como dice el archivo y guarda. Nada se guarda antes.',
              'Choose the current file. The app shows the new Big Bags, the ones the file already assigned and how it differs from the app. Tick what you want to correct as the file says and save. Nothing is saved before.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          className="text-apagado hover:text-tinta"
          aria-label={tr('Cerrar', 'Close')}
        >
          ×
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ElegirArchivo
          etiqueta={tr('Consolidated Shipments (.xlsx)', 'Consolidated Shipments (.xlsx)')}
          accept=".xlsx,.xlsm"
          archivos={archivo ? [archivo] : []}
          deshabilitado={cargar.isPending}
          onElegir={([elegido]) => {
            if (!elegido) return;
            setArchivo(elegido);
            setResultado(null);
            setElecciones(SIN_ELECCIONES);
            setBorradas(null);
            void enviar('revisar', elegido, SIN_ELECCIONES);
          }}
        />
        {cargar.isPending && <span className="text-apagado">{tr('Leyendo el archivo…', 'Reading the file…')}</span>}
      </div>
      {error && (
        <p className="text-alerta" role="alert">
          {error}
        </p>
      )}

      {r && d && (
        <div className="space-y-3">
          <p
            role="status"
            className={r.aplicado ? 'rounded-md bg-verde-claro/60 p-2 font-medium text-verde-oscuro' : ''}
          >
            {r.aplicado
              ? tr(
                  `Listo: ${numero(r.bolsasNuevas)} Big Bags nuevos, ${numero(r.bolsasAsignadas)} asignados como el archivo, ${numero(r.bolsasCorregidas)} corregidos, ${numero(r.bolsasLiberadas)} liberados y ${numero(r.mesesCorregidos)} meses de molienda corregidos.`,
                  `Done: ${numero(r.bolsasNuevas)} new Big Bags, ${numero(r.bolsasAsignadas)} assigned as in the file, ${numero(r.bolsasCorregidas)} corrected, ${numero(r.bolsasLiberadas)} released and ${numero(r.mesesCorregidos)} grinding months corrected.`,
                )
              : tr(
                  `Revisión (todavía no se guardó nada): ${numero(r.bolsasLeidas)} Big Bags en el archivo; ${numero(r.bolsasNuevas)} nuevos; ${numero(asignar)} se asignan como el archivo.`,
                  `Review (nothing saved yet): ${numero(r.bolsasLeidas)} Big Bags in the file; ${numero(r.bolsasNuevas)} new; ${numero(asignar)} get assigned as in the file.`,
                )}
          </p>
          {r.bolsasExportadas > 0 && (
            <p className="text-xs text-apagado" data-prueba="bolsas-exportadas">
              {r.aplicado
                ? tr(
                    `${numero(r.bolsasExportadas)} Big Bags pasaron de asignados a exportados: su contenedor ya se llenó.`,
                    `${numero(r.bolsasExportadas)} Big Bags went from assigned to exported: their container was already filled.`,
                  )
                : tr(
                    `${numero(r.bolsasExportadas)} Big Bags pasan de asignados a exportados: el archivo trae la fecha de llenado de su contenedor.`,
                    `${numero(r.bolsasExportadas)} Big Bags go from assigned to exported: the file has their container fill date.`,
                  )}
            </p>
          )}
          {r.bolsasConContenedor > 0 && (
            <p className="text-xs text-apagado" data-prueba="bolsas-con-contenedor">
              {r.aplicado
                ? tr(
                    `${numero(r.bolsasConContenedor)} Big Bags quedaron en el contenedor que dice el archivo.`,
                    `${numero(r.bolsasConContenedor)} Big Bags were placed in the container the file says.`,
                  )
                : tr(
                    `${numero(r.bolsasConContenedor)} Big Bags de su misma exportación reciben el contenedor que trae el archivo.`,
                    `${numero(r.bolsasConContenedor)} Big Bags of the same export get the container the file has.`,
                  )}
            </p>
          )}
          {r.bolsasConEntrada > 0 && (
            <p className="text-xs text-apagado" data-prueba="bolsas-con-entrada">
              {r.aplicado
                ? tr(
                    `${numero(r.bolsasConEntrada)} Big Bags recibieron la fecha de ingreso a bodega del archivo (Entry Date).`,
                    `${numero(r.bolsasConEntrada)} Big Bags got the warehouse entry date from the file (Entry Date).`,
                  )
                : tr(
                    `${numero(r.bolsasConEntrada)} Big Bags sin fecha de ingreso a bodega reciben la del archivo (Entry Date).`,
                    `${numero(r.bolsasConEntrada)} Big Bags without a warehouse entry date get the one in the file (Entry Date).`,
                  )}
            </p>
          )}
          {r.exportacionesCreadas.length > 0 && (
            <p className="text-xs text-apagado">
              {tr('Exportaciones nuevas: ', 'New exports: ')}
              {r.exportacionesCreadas.join(', ')}
            </p>
          )}

          {d.exportaciones.length === 0 && d.mesesDeMolienda.length === 0 ? (
            <p className="text-verde-oscuro">
              {tr(
                'Las exportaciones y los meses de molienda coinciden con la app.',
                'Exports and grinding months match the app.',
              )}
            </p>
          ) : (
            <>
              {d.exportaciones.length > 0 && (
                <div className="max-h-[24rem] overflow-auto rounded-md border border-linea">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                        <th className="px-2 py-1 font-semibold">
                          {tr('En el archivo', 'In the file')}
                          <NotaDeAyuda texto={AYUDA_CONSOLIDADO.enElArchivo} />
                        </th>
                        <th className="px-2 py-1 font-semibold">
                          {tr('En la app', 'In the app')}
                          <NotaDeAyuda texto={AYUDA_CONSOLIDADO.enLaApp} />
                        </th>
                        <th className="px-2 py-1 text-right font-semibold">
                          BB
                          <NotaDeAyuda texto={AYUDA_CONSOLIDADO.bb} />
                        </th>
                        <th className="px-2 py-1 font-semibold">
                          {tr('Qué hacer', 'What to do')}
                          <NotaDeAyuda texto={AYUDA_CONSOLIDADO.queHacer} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.exportaciones.map(g => (
                        <FilaDeDiferencia
                          key={`${g.archivo ?? ''}|${g.app ?? ''}`}
                          grupo={g}
                          elecciones={elecciones}
                          deshabilitado={r.aplicado || cargar.isPending}
                          onCambiar={setElecciones}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {d.mesesDeMolienda.length > 0 && (
                <div className="rounded-md border border-linea p-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-medium">
                      {tr('Mes de molienda distinto', 'Different grinding month')}
                      <NotaDeAyuda texto={AYUDA_CONSOLIDADO.mesDeMolienda} />
                    </h3>
                    {!r.aplicado && (
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={elecciones.meses.length === d.mesesDeMolienda.length}
                          onChange={e =>
                            setElecciones(x => ({
                              ...x,
                              meses: e.target.checked ? d.mesesDeMolienda.map(m => m.bb) : [],
                            }))
                          }
                        />
                        {tr('Corregir todos como el archivo', 'Correct all as in the file')}
                      </label>
                    )}
                  </div>
                  <ul className="mt-1 max-h-40 overflow-auto text-xs">
                    {d.mesesDeMolienda.map(m => (
                      <li key={m.bb}>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            disabled={r.aplicado}
                            checked={elecciones.meses.includes(m.bb)}
                            onChange={e =>
                              setElecciones(x => ({...x, meses: alternar(x.meses, m.bb, e.target.checked)}))
                            }
                          />
                          <span>
                            <b>{m.bb}</b>: {tr('archivo', 'file')} {m.archivo ?? '—'} · app {m.app ?? '—'}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {hayQueGuardar && (
            <button
              type="button"
              disabled={cargar.isPending}
              onClick={() => void enviar('aplicar')}
              className="rounded-md bg-verde px-3 py-1.5 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {elegidas
                ? tr(
                    `Guardar con ${numero(elegidas)} correcciones elegidas`,
                    `Save with ${numero(elegidas)} chosen corrections`,
                  )
                : tr('Guardar', 'Save')}
            </button>
          )}

          {r.aplicado && d.exportacionesQueQuedanVacias.length > 0 && (
            <div className="rounded-md border border-alerta/50 p-2">
              <p>
                {tr(
                  `Quedaron sin Big Bags: ${d.exportacionesQueQuedanVacias.join(', ')}.`,
                  `Left without Big Bags: ${d.exportacionesQueQuedanVacias.join(', ')}.`,
                )}
              </p>
              {borradas ? (
                <p role="status" className="mt-1 font-medium text-verde-oscuro">
                  {borradas}
                </p>
              ) : !permisos.administrar ? (
                // Borrar exportaciones es solo de administradores (decision 0026).
                <p className="mt-1 text-xs text-apagado">
                  {tr('Un administrador puede borrarlas.', 'An administrator can delete them.')}
                </p>
              ) : (
                <button
                  type="button"
                  disabled={eliminar.isPending}
                  onClick={() => void borrarVacias(d.exportacionesQueQuedanVacias)}
                  className="mt-1 rounded-md border border-alerta px-3 py-1 text-alerta hover:bg-alerta/10 disabled:opacity-50"
                >
                  {tr('Borrar esas exportaciones', 'Delete those exports')}
                </button>
              )}
            </div>
          )}

          {r.problemas.length > 0 && (
            <details>
              <summary className="cursor-pointer text-apagado">
                {tr('Otros avisos de la lectura', 'Other reading notices')} (
                {r.problemas.map(p => `${p.categoria}: ${numero(p.cantidad)}`).join(', ')})
              </summary>
              <ul className="mt-1 list-disc pl-5 text-xs text-apagado">
                {r.ejemplos.map(m => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

function FilaDeDiferencia({
  grupo: g,
  elecciones,
  deshabilitado,
  onCambiar,
}: {
  grupo: DiferenciaDeExportacion;
  elecciones: EleccionesDelConsolidado;
  deshabilitado: boolean;
  onCambiar: (f: (x: EleccionesDelConsolidado) => EleccionesDelConsolidado) => void;
}) {
  const sin = <span className="text-apagado">{tr('sin exportación', 'no export')}</span>;
  return (
    <tr className="border-t border-linea/70 align-top">
      <td className="px-2 py-1">
        {g.archivo ?? sin}
        {g.exportadaEnElArchivo && <span className="ml-1 text-xs text-apagado">(Exported)</span>}
      </td>
      <td className="px-2 py-1">{g.app ?? sin}</td>
      <td className="px-2 py-1 text-right">
        <details>
          <summary className="cursor-pointer">{numero(g.bolsas.length)}</summary>
          <p className="max-w-xs text-left text-xs text-apagado">{g.bolsas.join(', ')}</p>
        </details>
      </td>
      <td className="px-2 py-1">
        {g.tipo === 'se_asigna' && (
          <span className="text-apagado">{tr('Se asigna como el archivo', 'Gets assigned as in the file')}</span>
        )}
        {g.tipo === 'corregible' && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={deshabilitado}
              checked={elecciones.corregir.includes(g.archivo!)}
              onChange={e => onCambiar(x => ({...x, corregir: alternar(x.corregir, g.archivo!, e.target.checked)}))}
            />
            {tr(`Pasar a ${g.archivo} como el archivo`, `Move to ${g.archivo} as in the file`)}
          </label>
        )}
        {g.tipo === 'solo_en_la_app' && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={deshabilitado}
              checked={elecciones.liberar.includes(g.app!)}
              onChange={e => onCambiar(x => ({...x, liberar: alternar(x.liberar, g.app!, e.target.checked)}))}
            />
            {tr(`Quitar de ${g.app} (quedan disponibles)`, `Remove from ${g.app} (back to available)`)}
          </label>
        )}
      </td>
    </tr>
  );
}
